import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Course } from '../data/courses';
import type { AgeGroup } from '../types/child';
import { getCoursesIfLoaded, prefetchCourses } from '../data/courses-loader';
import { useParentCurriculumStore } from './parentCurriculumStore';
import { useReviewStore } from './reviewStore';

interface LessonProgress {
  lessonId: string;
  completed: boolean;
  quizScore: number; // 0-100
  stars: number; // 1-3 based on quiz score
  completedAt: string;
}

/**
 * One CourseProgress row = one child × one course × one tier. When a child
 * auto-advances (see `lib/tier-progression.ts`), the old row stays in place
 * (tier = prior tier) as historical record, and a fresh row gets created
 * the next time they enter the course at the new tier. Untagged rows
 * (missing `tier`) are legacy from before the tiering feature shipped and
 * are treated as the child's current tier on read.
 */
interface CourseProgress {
  courseId: string;
  childId: string;
  /** The child's effective tier when this progress was recorded. */
  tier?: AgeGroup;
  lessonsCompleted: LessonProgress[];
  graduated: boolean;
  graduatedAt?: string;
  startedAt: string;
}

function starsFromScore(score: number): number {
  if (score >= 90) return 3;
  if (score >= 70) return 2;
  return 1;
}

/**
 * A progress row matches the current tier if (a) its tier exactly matches
 * the active tier, OR (b) it has no tier set (legacy/pre-tiering data) —
 * treat untagged as current so existing users see their progress.
 */
function matchesTier(p: { tier?: AgeGroup }, tier: AgeGroup): boolean {
  return p.tier === undefined || p.tier === tier;
}

function seedCourseLessonReviews(childId: string, courseId: string, lessonId: string, tier: AgeGroup): void {
  const seedFromCourses = (courses: Course[]) => {
    const course = courses.find((candidate) => candidate.id === courseId);
    if (course) {
      useReviewStore.getState().seedCourseLesson(childId, course, lessonId, tier);
    }
  };

  const loaded = getCoursesIfLoaded();
  if (loaded) {
    seedFromCourses(loaded);
    return;
  }

  void prefetchCourses().then(seedFromCourses);
}

interface CourseState {
  progress: CourseProgress[];

  startCourse: (childId: string, courseId: string, tier: AgeGroup) => void;
  completeLesson: (childId: string, courseId: string, lessonId: string, quizScore: number, tier: AgeGroup) => void;
  graduateCourse: (childId: string, courseId: string, tier: AgeGroup) => void;

  /**
   * Returns the row for the child's CURRENT tier. Untagged legacy rows
   * also match (they predate the tiering feature).
   */
  getCourseProgress: (childId: string, courseId: string, tier: AgeGroup) => CourseProgress | undefined;
  /** All graduated rows, regardless of tier — used by hall of fame, history. */
  getAllGraduations: (childId: string) => CourseProgress[];
  /** Graduated rows AT a specific tier — used by the auto-advance check. */
  isCourseGraduatedAtTier: (childId: string, courseId: string, tier: AgeGroup) => boolean;
  getCompletedCourses: (childId: string, tier: AgeGroup) => CourseProgress[];
  getActiveCourses: (childId: string, tier: AgeGroup) => CourseProgress[];
  isLessonCompleted: (childId: string, courseId: string, lessonId: string, tier: AgeGroup) => boolean;
  getNextLesson: (childId: string, courseId: string, tier: AgeGroup) => string | null;
  /**
   * Stamp every untagged progress row for a child with the given tier.
   * Called by the tier-advance orchestrator right before `child.ageGroup`
   * bumps, so old progress is locked to the prior tier and the new tier
   * starts empty.
   */
  freezeUntaggedAtTier: (childId: string, tier: AgeGroup) => void;
}

export const useCourseStore = create<CourseState>()(
  persist(
    (set, get) => ({
      progress: [],

      startCourse: (childId, courseId, tier) => {
        const existing = get().progress.find(
          (p) => p.childId === childId && p.courseId === courseId && matchesTier(p, tier)
        );
        if (existing) return; // Already started at this tier

        set((state) => ({
          progress: [
            ...state.progress,
            {
              courseId,
              childId,
              tier,
              lessonsCompleted: [],
              graduated: false,
              startedAt: new Date().toISOString(),
            },
          ],
        }));
      },

      completeLesson: (childId, courseId, lessonId, quizScore, tier) => {
        // Ensure a row exists at this tier — create if first lesson.
        const exists = get().progress.some(
          (p) => p.childId === childId && p.courseId === courseId && matchesTier(p, tier)
        );
        if (!exists) {
          set((state) => ({
            progress: [
              ...state.progress,
              {
                courseId,
                childId,
                tier,
                lessonsCompleted: [],
                graduated: false,
                startedAt: new Date().toISOString(),
              },
            ],
          }));
        }
        set((state) => ({
          progress: state.progress.map((p) => {
            if (p.childId !== childId || p.courseId !== courseId || !matchesTier(p, tier)) return p;

            const existingIdx = p.lessonsCompleted.findIndex(
              (l) => l.lessonId === lessonId
            );

            const lessonProgress: LessonProgress = {
              lessonId,
              completed: true,
              quizScore,
              stars: starsFromScore(quizScore),
              completedAt: new Date().toISOString(),
            };

            const updatedLessons = [...p.lessonsCompleted];
            if (existingIdx >= 0) {
              if (quizScore > updatedLessons[existingIdx].quizScore) {
                updatedLessons[existingIdx] = lessonProgress;
              }
            } else {
              updatedLessons.push(lessonProgress);
            }

            return { ...p, lessonsCompleted: updatedLessons };
          }),
        }));
        useParentCurriculumStore.getState().markLessonCompleted(childId, courseId, lessonId, tier);
        seedCourseLessonReviews(childId, courseId, lessonId, tier);
      },

      graduateCourse: (childId, courseId, tier) => {
        set((state) => ({
          progress: state.progress.map((p) => {
            if (p.childId !== childId || p.courseId !== courseId || !matchesTier(p, tier)) return p;
            if (p.graduated) return p;
            return {
              ...p,
              graduated: true,
              tier,
              graduatedAt: new Date().toISOString(),
            };
          }),
        }));
      },

      getCourseProgress: (childId, courseId, tier) => {
        return get().progress.find(
          (p) => p.childId === childId && p.courseId === courseId && matchesTier(p, tier)
        );
      },

      getAllGraduations: (childId) => {
        return get().progress.filter((p) => p.childId === childId && p.graduated);
      },

      isCourseGraduatedAtTier: (childId, courseId, tier) => {
        return get().progress.some(
          (p) => p.childId === childId && p.courseId === courseId && p.graduated && p.tier === tier
        );
      },

      getCompletedCourses: (childId, tier) => {
        return get().progress.filter(
          (p) => p.childId === childId && p.graduated && matchesTier(p, tier)
        );
      },

      getActiveCourses: (childId, tier) => {
        return get().progress.filter(
          (p) => p.childId === childId && !p.graduated && matchesTier(p, tier)
        );
      },

      isLessonCompleted: (childId, courseId, lessonId, tier) => {
        const cp = get().progress.find(
          (p) => p.childId === childId && p.courseId === courseId && matchesTier(p, tier)
        );
        if (!cp) return false;
        return cp.lessonsCompleted.some(
          (l) => l.lessonId === lessonId && l.completed
        );
      },

      freezeUntaggedAtTier: (childId, tier) => {
        set((state) => ({
          progress: state.progress.map((p) =>
            p.childId === childId && p.tier === undefined ? { ...p, tier } : p,
          ),
        }));
      },

      getNextLesson: (childId, courseId, tier) => {
        const cp = get().progress.find(
          (p) => p.childId === childId && p.courseId === courseId && matchesTier(p, tier)
        );
        // Lazy COURSES lookup. Returns null until the courses bundle
        // loads — the only synchronous callers (CoursesPage, scheduler,
        // ContinueRail) either trigger prefetch on mount or tolerate the
        // null + re-render once the bundle hydrates. See courses-loader.ts.
        const courses: Course[] | null = getCoursesIfLoaded();
        if (!courses) {
          void prefetchCourses();
          return null;
        }
        const course = courses.find((c) => c.id === courseId);
        if (!course) return null;
        if (!cp) return course.lessons[0]?.id ?? null;

        const completedIds = new Set(
          cp.lessonsCompleted.filter((l) => l.completed).map((l) => l.lessonId)
        );

        for (const lesson of course.lessons) {
          if (!completedIds.has(lesson.id)) {
            return lesson.id;
          }
        }

        return null; // All lessons completed
      },
    }),
    { name: 'homeschool-courses', version: 0, migrate: (state) => state }
  )
);
